import React, { useRef, useState, useEffect, useContext, useMemo } from 'react'
import { Box, Typography, Button, Collapse } from '@mui/material'
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from 'framer-motion'

import { IntakeContext } from 'context/IntakeContext'
import { useDashboardCalculations } from '../../dashboard/hooks/useDashboardCalculations'
import {
  format,
  addDays,
  max,
  addYears,
  differenceInYears,
  differenceInMonths,
  addMonths,
  parseISO,
  getMonth,
  getDate,
} from 'date-fns'

import Card from 'atoms/Card'
import { useFormDebounce } from 'hooks/useFormDebounce'

function parseLocalDate(input) {
  if (!input) return null
  if (input instanceof Date) {
    // Already a Date, normalize to local midnight
    return new Date(input.getFullYear(), input.getMonth(), input.getDate())
  }
  if (typeof input === 'string') {
    // Parse "YYYY-MM-DD"
    const [year, month, day] = input.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  throw new Error('parseLocalDate: unsupported input type')
}
function formatLocalDate(date) {
  if (!date) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// TODO: MAKE MIN AND MAX AGE PULLED FROM INTAKE STATE / GENERATED

// TODO: MAKE STOPS DYNAMICALLY GENERATED

const getAgeFromDate = (dateOfBirth, date) => {
  const birth = parseLocalDate(dateOfBirth)
  const target = parseLocalDate(date)
  let age = target.getFullYear() - birth.getFullYear()
  const hasHadBirthday =
    target.getMonth() > birth.getMonth() ||
    (target.getMonth() === birth.getMonth() &&
      target.getDate() >= birth.getDate())
  // adjust for birthday in past
  // return hasHadBirthday ? age : age-1
  return age
}

const isValidDate = (d) =>
  new Date(d) instanceof Date && !Number.isNaN(new Date(d).getTime())

const getRetirementDateFromAge = (dateOfBirth, age) => {
  const birth = parseLocalDate(dateOfBirth)
  const retirement = new Date(birth)
  retirement.setFullYear(birth.getFullYear() + age)
  return retirement
}

export default function RetirementSlider() {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const { firstFullyEligibleDate, eligibilityScenarioMRA10Date } =
    useDashboardCalculations()

  // let stops = [
  //   { age: currentAge, type: '', label: 'Now' },
  //   { age: plannedAge, type: 'primary', label: 'Goal' },
  //   { age: 75, type: '', label: `Age 75` },
  //   // { age: 62, type: 'milestone', label: 'MRA+10' },
  //   // { age: 65, type: 'milestone', label: 'SS Eligible' },
  //   // { age: 70, type: '', label: 'Age 70' },
  // ]

  const boosterAge =
    isValidDate(intakeState?.dateOfHire) &&
    isValidDate(intakeState?.dateOfBirth) &&
    getAgeFromDate(
      intakeState?.dateOfBirth,
      max([
        addYears(parseLocalDate(intakeState?.dateOfHire), 20),
        addYears(parseLocalDate(intakeState?.dateOfBirth), 62),
      ])
    )
  // console.log('boosterAge', boosterAge)

  const firstFullyEligibleAge = getAgeFromDate(
    intakeState?.dateOfBirth,
    intakeState?.retirementType === 'vera'
      ? max([
          parseLocalDate(new Date()),
          parseLocalDate(intakeState?.dateOfPlannedRetirement),
        ])
      : max([
          parseLocalDate(new Date()),
          parseLocalDate(firstFullyEligibleDate),
        ])
  )
  // console.log('firstFullyEligibleAge', firstFullyEligibleAge)

  const mra10Age = getAgeFromDate(
    intakeState?.dateOfBirth,
    parseLocalDate(eligibilityScenarioMRA10Date || new Date())
  )
  // console.log('eligibilityScenarioMRA10Date', eligibilityScenarioMRA10Date)
  // console.log('mra10Age', mra10Age)

  // make sure min date on graph is the first fully eligible date or today
  // adjust for VERA - assumed to be full retirement but if they have VERA adjust for date of planned retirement instead
  const currentAge = getAgeFromDate(intakeState?.dateOfBirth, new Date())
  // make sure planned age is greater than today and first fully eligible date
  // adjust for VERA - assumed to be full retirement but if they have VERA adjust for date of planned retirement instead
  const plannedAge = getAgeFromDate(
    intakeState?.dateOfBirth,
    intakeState?.retirementType === 'vera'
      ? max([
          parseLocalDate(new Date()),
          parseLocalDate(intakeState?.dateOfPlannedRetirement || new Date()),
        ])
      : max([
          parseLocalDate(new Date()),
          parseLocalDate(intakeState?.dateOfPlannedRetirement || new Date()),
          //remove firstFullyEligibleDate and just use dateOfPlannedRetirement
          // new Date(firstFullyEligibleDate),
        ])
  )
  const differenceInYears = Math.round(
    differenceInMonths(new Date(firstFullyEligibleDate), new Date()) / 12
  )

  const [value, setValue] = useState(plannedAge)
  const [initialValue, setInitialValue] = useState(plannedAge)
  const [initialDateValue, setInitialDateValue] = useState(
    intakeState?.dateOfPlannedRetirement
  )
  const [stops, setStops] = useState(() => {
    const stops = [
      { age: currentAge, type: '', label: '' },
      { age: plannedAge, type: 'primary', label: '' },
      { age: 70, type: '', label: 'Age 70' },
    ]

    if (firstFullyEligibleAge > currentAge && isGovernmentEmployee) {
      stops.push({
        age: firstFullyEligibleAge,
        type: 'milestone',
        label: 'First Fully Eligible Age',
      })
    }

    if (mra10Age > currentAge && isGovernmentEmployee) {
      stops.push({
        age: mra10Age,
        type: 'milestone',
        label: 'MRA + 10',
      })
    }

    if (boosterAge > currentAge && isGovernmentEmployee) {
      stops.push({
        age: boosterAge,
        type: 'milestone',
        label: 'Booster',
      })
    }

    // Sort by age to ensure correct order
    stops.sort((a, b) => a.age - b.age)

    return stops
  })

  const trackRef = useRef(null)
  const labelRefs = useRef({})

  const primaryStop = stops.find((s) => s.type === 'primary')
  const [previewValue, setPreviewValue] = useState(
    primaryStop?.age ?? plannedAge
  )
  const [hoveredMilestone, setHoveredMilestone] = useState(null)

  const [trackWidth, setTrackWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [hasEdited, setHasEdited] = useState(false)

  const showSave = value !== initialValue

  const sortedStops = [...stops].sort((a, b) => a.age - b.age)
  const firstStop = sortedStops[0]
  const lastStop = sortedStops[sortedStops.length - 1]

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        const width = trackRef.current.getBoundingClientRect().width
        setTrackWidth(width)
      }
    }

    measure() // initial attempt

    // Try again after layout stabilizes
    const timeout = setTimeout(measure, 500)

    const resizeObserver = new ResizeObserver(measure)
    if (trackRef.current) {
      resizeObserver.observe(trackRef.current)
      // console.log(trackWidth)
    }

    return () => {
      resizeObserver.disconnect()
      clearTimeout(timeout)
    }
  }, [])

  const x = useMotionValue(0)

  const valueToX = (val) =>
    ((val - firstStop.age) / (lastStop.age - firstStop.age)) * trackWidth

  const xToValue = (px) =>
    Math.round(
      (px / trackWidth) * (lastStop.age - firstStop.age) + firstStop.age
    )

  useEffect(() => {
    if (trackWidth > 0) {
      animate(x, valueToX(value), { duration: 0.25 })
      setPreviewValue(value)
    }
  }, [value, trackWidth])

  // keep date of planned retirement until changed by user
  useEffect(() => {
    const dob = parseLocalDate(intakeState?.dateOfBirth)
    const dopr = parseLocalDate(intakeState?.dateOfPlannedRetirement)
    if (!dopr) return

    // Detect if dateOfPlannedRetirement changed externally (e.g., from context)
    const plannedAgeFromContext = getAgeFromDate(dob, dopr)
    if (!hasEdited && plannedAgeFromContext !== initialValue) {
      // External change → rebuild internal state

      setInitialValue(plannedAgeFromContext)
      setValue(plannedAgeFromContext)
      setPreviewValue(plannedAgeFromContext)
      setInitialDateValue(formatLocalDate(dopr))
      setHasEdited(false)
      return // stop here so it doesn't trigger the below block
    }

    // Normal user-driven update behavior
    const isChangedBySlider =
      getMonth(dob) === getMonth(dopr) && getDate(dob) === getDate(dopr)
    const showChange = previewValue !== initialValue

    if (isChangedBySlider || showChange) {
      updateIntakeStateAtKey({
        dateOfPlannedRetirement: formatLocalDate(
          getRetirementDateFromAge(intakeState?.dateOfBirth, previewValue)
        ),
      })
    }
  }, [previewValue, intakeState?.dateOfPlannedRetirement, hasEdited])

  const handleDrag = (_, info) => {
    setIsDragging(true)
    const rect = trackRef.current.getBoundingClientRect()
    const deltaX = info.point.x - rect.left
    const clampedX = Math.max(0, Math.min(deltaX, trackWidth))
    const val = xToValue(clampedX)

    setPreviewValue(val)

    if (val !== initialValue) {
      setHasEdited(true)
    }
  }

  const handleDragEnd = (_, info) => {
    setIsDragging(false)
    const rect = trackRef.current.getBoundingClientRect()
    const deltaX = info.point.x - rect.left
    const clampedX = Math.max(0, Math.min(deltaX, trackWidth))
    const val = xToValue(clampedX)

    setValue(val)

    if (val !== initialValue) {
      setHasEdited(true)
    }
  }

  const handleClick = (e) => {
    const rect = trackRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clampedX = Math.max(0, Math.min(clickX, trackWidth))
    const val = xToValue(clampedX)
    setValue(val)

    if (val !== initialValue) {
      setHasEdited(true)
    }
  }

  const currentStop = stops.find((s) => s.age === previewValue) ?? null

  const visibleStops = [
    firstStop,
    ...(currentStop &&
    currentStop.age !== firstStop.age &&
    currentStop.age !== lastStop.age
      ? [currentStop]
      : []),
    lastStop,
  ]
  const labelStops = useMemo(() => {
    if (isDragging) return stops

    const intermediateStops = []

    if (
      currentStop &&
      currentStop.age !== firstStop.age &&
      currentStop.age !== lastStop.age
    ) {
      intermediateStops.push(currentStop)
    }

    if (
      hoveredMilestone &&
      ![firstStop.age, lastStop.age, currentStop?.age].includes(
        hoveredMilestone.age
      )
    ) {
      intermediateStops.push(hoveredMilestone)
    }

    return [firstStop, ...intermediateStops, lastStop]
  }, [isDragging, stops, firstStop, lastStop, currentStop, hoveredMilestone])

  const [labelOffsets, setLabelOffsets] = useState({})

  useEffect(() => {
    const offsets = {}
    const labels = labelStops.map((stop) => ({
      label: stop.label,
      rect: labelRefs.current[stop.label]?.getBoundingClientRect(),
    }))

    for (let i = 1; i < labels.length; i++) {
      const prev = labels[i - 1]
      const curr = labels[i]

      if (!prev.rect || !curr.rect) continue

      const isOverlapping =
        curr.rect.left < prev.rect.right && curr.rect.right > prev.rect.left

      if (isOverlapping) {
        offsets[curr.label] = (offsets[prev.label] || 0) + 14 // stack 14px lower
      }
    }

    setLabelOffsets(offsets)
  }, [labelStops, trackWidth])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      style={{
        cursor:
          intakeState?.rifPlan?.rifStatus === true ? 'not-allowed' : 'unset',
      }}
    >
      <Typography display="flex" align="left" variant="body2FunHeader" mb={2}>
        RETIREMENT AGE
      </Typography>

      <Card>
        <Box
          sx={{
            padding: { xs: '18px', md: '24px' },

            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
          }}
        >
          {/* title */}
          <Box display="flex" alignItems={'center'}>
            {showSave && (
              <Typography variant="body1Medium" color="#2370ED">
                {'*'}
              </Typography>
            )}
            <Typography
              textAlign={'left'}
              variant="body1Medium"
              color={'#262627'}
            >
              Retiring at Age {previewValue}
            </Typography>
            <Typography pl={1} variant="body2" color={'#75787B'}>
              {previewValue - stops[0].age === 0
                ? '(this year)'
                : `(in ${previewValue - stops[0].age} ${
                    previewValue - stops[0].age > 1 ? 'years' : 'year'
                  })`}
            </Typography>
          </Box>

          {/* slider */}
          <Box
            ref={trackRef}
            onClick={handleClick}
            sx={{
              marginTop: '16px',
              marginBottom: '24px',
              position: 'relative',
              width: '100%',
              //   maxWidth: 400,
              textAlign: 'left',
              height: 10,
              borderRadius: 5,
              bgcolor: '#F1F1F1',
              cursor:
                intakeState?.rifPlan?.rifStatus === true
                  ? 'not-allowed'
                  : 'pointer',
              pointerEvents:
                intakeState?.rifPlan?.rifStatus === true ? 'none' : 'auto',
            }}
          >
            {stops
              .filter((s) => s.age >= firstStop.age && s.age <= lastStop.age)

              .map((stop, i) => {
                const left = `${valueToX(stop.age)}px`

                if (stop.type !== '') {
                  return (
                    <Box
                      key={i}
                      onMouseEnter={() => setHoveredMilestone(stop)}
                      onMouseLeave={() => setHoveredMilestone(null)}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left,
                        transform: 'translate(-50%, -50%)',
                        width: 30,
                        height: 40,
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          width: stop.type === 'milestone' ? 8 : 4,
                          height: stop.type === 'milestone' ? 8 : 16,
                          bgcolor:
                            stop.type === 'milestone' ? '#D2D6DA' : '#495057',
                          borderRadius:
                            stop.type === 'milestone' ? '50%' : '20px',
                        }}
                      />
                    </Box>
                  )
                }
              })}

            {/* Thumb */}
            <motion.div
              drag="x"
              dragConstraints={{ left: 0, right: trackWidth }}
              dragElastic={0}
              dragMomentum={false}
              onDrag={handleDrag}
              onDragEnd={handleDragEnd}
              style={{
                zIndex: 10,
                x,
                position: 'absolute',
                top: '50%',
                translateY: '-50%',
                translateX: '-50%',
                cursor: 'grab',
              }}
              whileTap={{ scale: 1.2 }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: '#2370ED',
                  //   border: '2px solid #1976d2',
                  borderRadius: '50%',
                  boxShadow: 2,
                  textAlign: 'center',
                }}
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 25 17.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.5261 4.96577L7.49157 8.00027L10.5261 11.0414L9.59186 11.9756L5.61654 8.00027L9.59186 4.02495L10.5261 4.96577Z"
                    fill="white"
                  />
                  <path
                    d="M14.6165 11.0342L17.651 7.99973L14.6165 4.95861L15.5507 4.02441L19.526 7.99973L15.5507 11.9751L14.6165 11.0342Z"
                    fill="white"
                  />
                </svg>
              </Box>
            </motion.div>

            {/* Value label */}
            <AnimatePresence initial={false}>
              {labelStops.map((stop, i) => {
                const xPos = valueToX(stop.age)

                return (
                  <motion.div
                    key={stop.label}
                    ref={(el) => (labelRefs.current[stop.label] = el)}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setValue(stop.age)
                      if (stop.age !== initialValue) setHasEdited(true)
                    }}
                    style={{
                      position: 'absolute',
                      top: 12 + (labelOffsets[stop.label] || 0),
                      x: xPos,
                      translateX:
                        stop.age === firstStop.age
                          ? '0%'
                          : stop.age === lastStop.age
                          ? '-100%'
                          : '-50%',
                      color: '#A3A7AA',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      zIndex: 10,
                      display: 'inline-block',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body3Medium">{stop.label}</Typography>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </Box>

          {/* cancel and save buttons */}
          <Collapse in={hasEdited}>
            <Box display={'flex'} gap={2} mt={3}>
              {/* CANCEL BUTTON */}

              <Box
                onClick={() => {
                  setValue(initialValue)
                  setHasEdited(false)
                  updateIntakeStateAndSubmit(() =>
                    updateIntakeStateAtKey({
                      dateOfPlannedRetirement: initialDateValue,
                    })
                  )()
                }}
                sx={{
                  height: 32,
                  display: 'flex',
                  cursor: 'pointer',
                  justifyContent: 'center',
                  alignItems: 'center',
                  verticalAlign: 'center',
                  textAlign: 'center',
                  borderRadius: '100px',
                  paddingX: '16px',
                  border: '1.5px solid #262627',
                }}
              >
                <Typography
                  sx={{
                    letterSpacing: 0.32,
                  }}
                  variant="body2Fun"
                  color="#262627"
                >
                  Cancel
                </Typography>
              </Box>

              {/* SAVE BUTTON */}
              <Box
                onClick={() => {
                  const newRetirementDate = getRetirementDateFromAge(
                    intakeState.dateOfBirth,
                    value
                  )

                  const newStops = [
                    { age: currentAge, type: '', label: '' },
                    { age: value, type: 'primary', label: '' },
                    { age: 70, type: '', label: 'Age 70' },
                  ]

                  if (
                    firstFullyEligibleAge > currentAge &&
                    isGovernmentEmployee
                  ) {
                    newStops.push({
                      age: firstFullyEligibleAge,
                      type: 'milestone',
                      label: 'First Fully Eligible Age',
                    })
                  }

                  if (mra10Age > currentAge && isGovernmentEmployee) {
                    newStops.push({
                      age: mra10Age,
                      type: 'milestone',
                      label: 'MRA + 10',
                    })
                  }

                  if (boosterAge > currentAge && isGovernmentEmployee) {
                    newStops.push({
                      age: boosterAge,
                      type: 'milestone',
                      label: 'Booster',
                    })
                  }

                  newStops.sort((a, b) => a.age - b.age)

                  setStops(newStops)

                  updateIntakeStateAndSubmit(() =>
                    updateIntakeStateAtKey({
                      dateOfPlannedRetirement:
                        formatLocalDate(newRetirementDate),
                      rifPlan: {
                        ...intakeState?.rifPlan,
                        storedDateOfPlannedRetirment:
                          formatLocalDate(newRetirementDate),
                      },
                    })
                  )()

                  setInitialDateValue(formatLocalDate(newRetirementDate))
                  setInitialValue(value)
                  setHasEdited(false)
                }}
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  justifyContent: 'center',
                  alignItems: 'center',
                  verticalAlign: 'center',
                  textAlign: 'center',
                  borderRadius: '100px',
                  paddingX: '16px',
                  bgcolor: '#2370ED',
                }}
              >
                <Typography
                  sx={{
                    letterSpacing: 0.32,
                  }}
                  variant="body2Fun"
                  color="#fff"
                >
                  Save
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Card>
    </motion.div>
  )
}
